import moment from 'moment';
import clone from 'lodash.clonedeep';

import {
  toCurrency,
  toPercentage,
  toNumber,
  toImageUrl,
  dateToNum,
  getDateFromString,
} from './format.util';

export function getCompareCardsData(cards, zoom) {
  return cards.map((card) => {
    const cardData = {
      id: card.id,
      color: card.color,
      image: toImageUrl(`cards/thumb_${card.id}`),
      label: card.label,
      year: card.year,
      player: card.player,
      set: card.set,
      variation: card.variation,
      condition: card.condition,
      number: card.number,
    };

    const data = {
      numSales: 0,
      roi: 0,
    };

    if (!zoom) {
      if (card && card.oldestSale && card.oldestSale.price) {
        data.originalValue = card.oldestSale.price;
      }

      data.numSales = card.numSales;
      data.marketValue = card.marketValue;
    } else if (card.dailySales) {
      let sortedDates = Object.keys(card.dailySales);

      sortedDates = sortedDates
        .filter((d) => {
          const startDate = zoom.start ? moment(zoom.start) : moment().subtract(zoom, 'days');
          const endDate = zoom.end ? moment(zoom.end) : moment();
          const date = moment(getDateFromString(d));

          const isValid = date.add(1, 'days') > startDate && date.subtract(1, 'days') < endDate;

          if (isValid) {
            data.numSales += card.dailySales[d].n;
          }

          return isValid;
        });

      sortedDates.sort((a, b) => dateToNum(a) - dateToNum(b));

      if (sortedDates.length > 0) {
        data.originalValue = card.dailySales[sortedDates[0]].p;
        data.marketValue = card.dailySales[sortedDates[sortedDates.length - 1]].p;
      }
    }

    if (data.marketValue && data.originalValue) {
      data.roi = (data.marketValue - data.originalValue) / data.originalValue;
    }

    cardData.pop = card.pop || null;
    cardData.popDisplay = card.pop ? toNumber(card.pop) : '-';

    cardData.marketCap = card.marketCap || null;
    cardData.marketCapDisplay = card.marketCap ? toCurrency(card.marketCap) : '-';

    cardData.numSales = data.numSales || 0;
    cardData.numSalesDisplay = toNumber(data.numSales) || '0';

    cardData.originalValue = data.originalValue || null;
    cardData.originalValueDisplay = data.originalValue ? toCurrency(data.originalValue) : '-';

    cardData.marketValue = data.marketValue || null;
    cardData.marketValueDisplay = data.marketValue ? toCurrency(data.marketValue) : '-';

    cardData.currentValue = card.currentValue || null;
    cardData.currentValueDisplay = card.currentValue ? toCurrency(card.currentValue) : '-';

    cardData.dollarChange = data.marketValue && data.originalValue ? data.marketValue - data.originalValue : 0;
    cardData.dollarChangeDisplay = data.marketValue && data.originalValue ? `${cardData.dollarChange > 0 ? '+' : ''}${toCurrency(data.marketValue - data.originalValue)}` : '$0';
    cardData.dollarChangeSuccess = cardData.dollarChange > 0;
    cardData.dollarChangeError = cardData.dollarChange < 0;

    cardData.percentChange = data.roi || 0;
    cardData.percentChangeDisplay = data.roi ? `${cardData.percentChange > 0 ? '+' : ''}${toPercentage(data.roi)}` : '0%';
    cardData.percentChangeSuccess = cardData.percentChange > 0;
    cardData.percentChangeError = cardData.percentChange < 0;

    return cardData;
  });
}

export function getCompareIndexesData(indexes, zoom) {
  return indexes.map((index) => {
    const indexData = {
      id: index.id,
      color: index.color,
      image: index.image,
      icon: index.icon,
      label: index.label,
    };

    let sortedIndexDates = Object.keys(index.dailyIndexTotal);

    if (zoom) {
      const startDate = zoom.start ? moment(zoom.start) : moment().subtract(zoom, 'days');
      const endDate = zoom.end ? moment(zoom.end) : moment();

      sortedIndexDates = sortedIndexDates
        .filter((d) => {
          const date = moment(getDateFromString(d));
          return date.add(1, 'days') > startDate && date.subtract(1, 'days') < endDate;
        });
    }

    sortedIndexDates.sort((a, b) => dateToNum(a) - dateToNum(b));

    indexData.originalValue = sortedIndexDates.length > 0 ? index.dailyIndexTotal[sortedIndexDates[0]] : null;
    indexData.originalValueDisplay = indexData.originalValue ? toNumber(indexData.originalValue) : '-';

    indexData.dailyIndex = index.dailyIndex;
    indexData.dailyIndexDisplay = indexData.dailyIndex ? toNumber(indexData.dailyIndex) : '-';

    indexData.dailySalesCount = index.dailySalesCount;
    indexData.dailySalesCountDisplay = indexData.dailySalesCount ? toNumber(indexData.dailySalesCount) : '-';

    indexData.dailySales = index.dailySales;
    indexData.dailySalesDisplay = indexData.dailySales ? toCurrency(indexData.dailySales) : '-';

    indexData.totalCards = index.totalCards;
    indexData.totalCardsDisplay = indexData.totalCards ? toNumber(indexData.totalCards) : '-';

    indexData.realChange = indexData.dailyIndex && indexData.originalValue ? indexData.dailyIndex - indexData.originalValue : 0;
    indexData.realChangeDisplay = indexData.dailyIndex && indexData.originalValue ? `${indexData.realChange > 0 ? '+' : ''}${toNumber(indexData.dailyIndex - indexData.originalValue)}` : '0';
    indexData.realChangeSuccess = indexData.realChange > 0;
    indexData.realChangeError = indexData.realChange < 0;

    indexData.percentChange = indexData.originalValue ? indexData.realChange / indexData.originalValue : 0;
    indexData.percentChangeDisplay = `${indexData.percentChange > 0 ? '+' : ''}${toPercentage(indexData.percentChange)}`;
    indexData.percentChangeSuccess = indexData.percentChange > 0;
    indexData.percentChangeError = indexData.percentChange < 0;

    return indexData;
  });
}

export function getCompareGraphData(items, zoom, type) {
  const datasets = [];
  const labels = [];
  const range = { start: new Date(), end: new Date() };
  const valueMapProperty = type === 'cards' ? 'dailySales' : 'dailyIndexTotal';

  if (!zoom) {
    let oldestDate = moment(new Date());
    let date = null;

    items.forEach((item) => {
      if (item[valueMapProperty] && Object.keys(item[valueMapProperty]).length) {
        Object.keys(item[valueMapProperty]).forEach((d) => {
          date = moment(getDateFromString(d));

          if (date < oldestDate) {
            oldestDate = date;
          }
        });
      }
    });

    range.start = oldestDate.toDate();
    range.end = new Date();
  } else {
    const zoomValue = clone(zoom);

    range.start = zoomValue.start ? zoomValue.start : moment().subtract(zoomValue, 'days').toDate();
    range.end = zoomValue.end ? zoomValue.end : new Date();
  }

  if (typeof range.start === 'object') {
    for (let i = range.start; i <= range.end; i.setDate(i.getDate() + 1)) {
      labels.push(moment(i).format('MM/DD/YYYY'));
    }
  }

  items.forEach((item) => {
    const prices = [];

    labels.forEach((d) => {
      let price = null;

      if (item[valueMapProperty] && item[valueMapProperty][d]) {
        price = typeof item[valueMapProperty][d] === 'object' ? item[valueMapProperty][d].p.toFixed(2) : item[valueMapProperty][d].toFixed(2);
      }

      prices.push(price);
    });

    const dataset = {
      label: item.label,
      pointRadius: 2,
      borderColor: item.color,
      borderWidth: 3,
      spanGaps: true,
      fill: false,
      data: prices,
      useBorderColorForPointColor: true,
    };

    datasets.push(dataset);
  });

  return {
    labels,
    datasets,
  };
}
